import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen,
  Clock,
  Briefcase,
  FileText,
  ChevronRight,
  Download,
  CheckSquare,
  Users,
  Target,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/App";

export default function Training() {
  const [modules, setModules] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [modulesRes, pillarsRes] = await Promise.all([
          api.get("/training"),
          api.get("/pillars"),
        ]);
        setModules(modulesRes.data);
        setPillars(pillarsRes.data);
      } catch (error) {
        // Error fetching training data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPillarName = (pillarId) => {
    const pillar = pillars.find((p) => p.id === pillarId);
    return pillar?.title || null;
  };

  return (
    <div data-testid="training-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="label-uppercase text-orange-400 mb-4">Leadership Resources</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="training-title">
              90-Day PPS Coherence Plan
            </h1>
            <p className="text-lg text-slate-300">
              A systems playbook in modular form. Each module delivers a leadership briefing 
              paired with an implementation kit, designed for action, not passive learning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What This Is / What This Isn't */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-sm border border-slate-200">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-emerald-500" />
                What Each Module Is
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <Briefcase className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                  <span><strong>Leadership Briefing:</strong> Strategic context and decision-making guidance</span>
                </li>
                <li className="flex items-start gap-2">
                  <Layers className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                  <span><strong>Implementation Kit:</strong> Ready-to-use protocols, templates, and action steps</span>
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                  <span><strong>Systems Playbook:</strong> Structured guidance for building coherent practices</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-100 p-6 rounded-sm border border-slate-200">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-5 h-5 text-slate-400 flex items-center justify-center font-bold">✕</span>
                What This Is Not
              </h3>
              <ul className="space-y-3 text-slate-500">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">—</span>
                  <span>Not a course to complete</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">—</span>
                  <span>Not traditional professional development</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">—</span>
                  <span>Not passive content consumption</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Featured: 90-Day Plan Download */}
      <section className="py-12 bg-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <p className="label-uppercase text-purple-200 mb-2">Complete Implementation Guide</p>
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Download the Full 90-Day Plan
              </h2>
              <p className="text-purple-100 mb-6">
                Get the complete 90-Day PPS Coherence Plan with all modules, week-by-week 
                action items, leadership briefings, and implementation kits in one document.
              </p>
              <Button className="bg-white text-purple-600 hover:bg-purple-50" data-testid="download-90-day-btn" asChild>
                <a href="/90-day-pps-coherence-plan.pdf" download="90-Day-PPS-Coherence-Plan.pdf">
                  <Download className="w-4 h-4 mr-2" />
                  Download Complete Plan (PDF)
                </a>
              </Button>
            </div>
            <div className="w-full lg:w-64 h-40 bg-purple-500/50 rounded-sm flex items-center justify-center">
              <BookOpen className="w-20 h-20 text-purple-200" strokeWidth={1} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
